/**
 * PremiumStatCard — KPI card fintech-grade.
 *
 * Uso pensado para el Panel CEO / Wallets: presenta un número grande con
 * fuente mono, sublabel, ícono con color contextual, y opcional una
 * micro-tendencia (delta %). Reutilizable en toda la sección Admin.
 *
 * Diseño según `/app/design_guidelines.json` archetype "Performance Pro
 * Fintech" — adaptado a light-mode para mantener coherencia con el resto
 * de la app (VentasPage / Pasajes no se tocan y usan light).
 *
 * Props:
 *   label:       string · Título corto en uppercase (kicker)
 *   value:       string|number · valor principal (se le da fuente mono)
 *   sublabel?:   string · texto pequeño debajo del valor
 *   icon?:       LucideIcon
 *   tone?:       'teal' (default) | 'emerald' | 'amber' | 'rose' | 'sky' | 'slate'
 *   delta?:      { value: number, positive?: boolean } · micro-tendencia
 *   loading?:    boolean · muestra skeleton
 *   onClick?:    fn · hace la card clickable
 *   testId?:     string
 */
import { Card } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

const TONE_STYLES = {
  teal:    { ring: 'hover:border-teal-400/60',    icon: 'bg-teal-100 text-teal-700',   accent: 'text-teal-700' },
  emerald: { ring: 'hover:border-emerald-400/60', icon: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-700' },
  amber:   { ring: 'hover:border-amber-400/60',   icon: 'bg-amber-100 text-amber-700', accent: 'text-amber-700' },
  rose:    { ring: 'hover:border-rose-400/60',    icon: 'bg-rose-100 text-rose-700',   accent: 'text-rose-700' },
  sky:     { ring: 'hover:border-sky-400/60',     icon: 'bg-sky-100 text-sky-700',     accent: 'text-sky-700' },
  slate:   { ring: 'hover:border-slate-400/60',   icon: 'bg-slate-100 text-slate-700', accent: 'text-slate-700' },
};

export const PremiumStatCard = ({
  label,
  value,
  sublabel,
  icon: Icon,
  tone = 'teal',
  delta,
  loading = false,
  onClick,
  testId,
}) => {
  const style = TONE_STYLES[tone] || TONE_STYLES.teal;
  const clickable = typeof onClick === 'function';

  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className={`
        group relative overflow-hidden
        border border-slate-200 shadow-sm bg-white
        p-5 sm:p-6 rounded-xl
        transition-all duration-200
        ${style.ring}
        ${clickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
          {label}
        </p>
        {Icon && (
          <span className={`p-1.5 rounded-lg ${style.icon}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-3 h-8 w-24 bg-slate-100 animate-pulse rounded" />
      ) : (
        <p className="mt-2 text-2xl sm:text-3xl font-mono font-bold text-slate-900 tabular-nums leading-none">
          {value}
        </p>
      )}

      <div className="mt-2 flex items-center justify-between gap-3 min-h-[16px]">
        {sublabel && (
          <p className="text-xs text-slate-500 truncate">{sublabel}</p>
        )}
        {delta && (
          <span
            className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
              delta.positive === false
                ? 'bg-rose-50 text-rose-700'
                : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {delta.positive === false
              ? <ArrowDownRight className="w-3 h-3" />
              : <ArrowUpRight className="w-3 h-3" />}
            {Math.abs(delta.value).toFixed(1)}%
          </span>
        )}
      </div>

      {/* Sutil highlight en hover */}
      <div
        className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
        aria-hidden
      />
    </Card>
  );
};

export default PremiumStatCard;
